/* eslint-disable no-return-assign */
import React from "react";
import { Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { numberFormatter } from "utils/HelperFunctions";

function DoughnutChart(props) {
  const { dataStructure, label } = props;
  const { labels, data, percentages, colors } = dataStructure;

  const getOrCreateTooltip = (chart) => {
    let tooltipEl = chart.canvas.parentNode.querySelector("div");

    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.style.background = "white";
      tooltipEl.style.borderRadius = "3px";
      tooltipEl.style.color = "black";
      tooltipEl.style.opacity = 1;
      tooltipEl.style.pointerEvents = "none";
      tooltipEl.style.position = "absolute";
      tooltipEl.style.transform = "translate(-50%, 0)";
      tooltipEl.style.transition = "all .1s ease";

      const table = document.createElement("table");
      table.style.margin = "0px";

      tooltipEl.appendChild(table);
      chart.canvas.parentNode.appendChild(tooltipEl);
    }

    return tooltipEl;
  };

  const externalTooltipHandler = (context) => {
    // Tooltip Element
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltip(chart);
    tooltipEl.style.border = "1px solid #888";
    tooltipEl.style.borderRadius = "0.5rem";
    // tooltipEl.style.display = "inline-block";
    tooltipEl.style.padding = 0;

    // Hide if no tooltip
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = 0;
      return;
    }

    // Set Text
    if (tooltip.body) {
      const titleLines = tooltip.title || [];
      const bodyLines = tooltip.body.map((b) => b.lines);

      const tableHead = document.createElement("thead");

      titleLines.forEach((title) => {
        const tr = document.createElement("tr");
        tr.style.borderWidth = 0;

        const th = document.createElement("th");
        th.style.borderWidth = 0;
        const text = document.createTextNode(title);

        th.appendChild(text);
        tr.appendChild(th);
        tableHead.appendChild(tr);
      });

      const tableBody = document.createElement("tbody");
      bodyLines.forEach((body, i) => {
        const ttcolors = tooltip.labelColors[i];

        const span = document.createElement("span");
        span.style.background = ttcolors.backgroundColor;
        span.style.borderColor = ttcolors.borderColor;
        span.style.borderWidth = "1px";
        span.style.marginRight = "10px";
        span.style.height = "10px";
        span.style.width = "10px";
        span.style.display = "inline-block";
        span.style.borderRadius = "5px";

        const tr = document.createElement("tr");
        tr.style.backgroundColor = "white";
        tr.style.borderWidth = 0;
        tr.style.lineHeight = "0.5rem";
        tr.style.whiteSpace = "nowrap";

        const td = document.createElement("td");
        td.style.borderWidth = 0;
        td.style.color = "#000";
        td.style.fontSize = "0.8rem";

        const text = document.createTextNode(body);

        td.appendChild(span);
        td.appendChild(text);
        tr.appendChild(td);
        tableBody.appendChild(tr);
      });

      const tableRoot = tooltipEl.querySelector("table");

      // Remove old children
      while (tableRoot.firstChild) {
        tableRoot.firstChild.remove();
      }

      // Add new children
      tableRoot.appendChild(tableHead);
      tableRoot.appendChild(tableBody);
    }

    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

    // Display, position, and set styles for font
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = `${positionX + tooltip.caretX}px`;
    tooltipEl.style.top = `${positionY + tooltip.caretY}px`;
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding = `${tooltip.options.padding}px ${tooltip.options.padding}px`;
  };

  const options = {
    cutout: "80%",
    responsive: true,
    maintainAspectRatio: true,
    radius: "100%",
    elements: {
      doughnut: {
        borderWidth: 2,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      // tooltip: {
      //   enabled: true,
      //   usePointStyle: true,
      //   position: "nearest",
      //   mode: "point",
      //   intersect: true,
      //   yPadding: 10,
      //   xPadding: 10,
      //   caretSize: 6,
      //   caretPadding: 10,
      //   backgroundColor: "white",
      //   bodyColor: "#000",
      //   bodyFontColor: "#000",
      //   borderColor: "#888",
      //   borderWidth: 1,
      //   yAlign: "bottom",
      //   titleColor: "black",
      //   footerColor: "black",
      // },
      tooltip: {
        enabled: false,
        position: "nearest",
        external: externalTooltipHandler,
      },
      datalabels: {
        color: "#000",
        align: "end",
        anchor: "end",
        formatter: (value, context) => {
          if (context.chart.data.percentages[context.dataIndex] >= 2) {
            return `${context.chart.data.percentages[context.dataIndex]}%`;
          }
          return "";
        },
        font: {
          size: 12,
        },
      },
    },
    layout: {
      padding: {
        left: 35,
        right: 30,
        top: 10,
        bottom: 10,
      },
    },
    scales: {
      y: {
        grid: {
          drawBorder: false,
          display: false,
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          display: false,
        },
      },
      x: {
        grid: {
          drawBorder: false,
          display: false,
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          display: false,
        },
      },
    },
    animation: {
      animateRotate: false,
    },
  };

  const plugins = [
    {
      afterDraw(chart) {
        const { ctx } = chart;
        const { width } = chart;
        const { height } = chart;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.font = "12px neuehaas";

        if (chart?.data?.datasets[0].data.length < 1) {
          ctx.fillText("No data to display", width / 2, height / 2);
          ctx.restore();
        } else {
          const count = chart.data.datasets[0].data.reduce((a, b) => a + b);
          ctx.font = "28px neuehaas";
          ctx.fontWeight = "700";
          ctx.fillText(numberFormatter(count), width / 2, height / 2 - height * 0.03);

          ctx.font = "12px neuehaas";
          ctx.fontWeight = "400";
          ctx.fillStyle = "#444";
          ctx.fillText(`Total ${label}`, width / 2, height / 2 + height * 0.04);
          ctx.save();
        }
      },
    },
    ChartDataLabels,
  ];
  return (
    <Doughnut
      data={{
        labels,
        percentages,
        datasets: [
          {
            data,
            backgroundColor: colors,
            borderColor: "#FFF",
            borderWidth: 1,
          },
        ],
      }}
      plugins={plugins}
      options={options}
    />
  );
}

export default DoughnutChart;
